import React, { useContext, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Button,
    Grid,
    GridColumn,
    Modal,
    ModalCloser,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Select,
    TextField,
    TMultipleSelectValue,
} from '../../../patterns';
import Datepicker from '../../../patterns/components/forms/datepicker/datepicker';
import { StoreContext } from '../base/root-store';
import {
    formatToYearAndMonthAndDayWithDateInput,
    getLocaleFromLanguage,
} from '../base/dateUtils';
import { DateRange } from 'react-day-picker';
import { getTooltipButton } from '../../../patterns/components/button/button';
import { Languages } from './questions-store';
import EmojiPicker from 'emoji-picker-react';

export const AnnouncementModal = observer((): JSX.Element => {
    const { questionStore: store, userStore, labelStore, sessionStore } = useContext(StoreContext);
    const initialAnnouncement = store.variables.announcementInEditing;
    const [isEmojiPickerOpen, setEmojiPickerOpen] = useState(false);
    const [emojiTargetLang, setEmojiTargetLang] = useState<string | null>(null);

    const isAppLevelAnnouncement = sessionStore.state === 'admin-session';

    const [targetRoles, setTargetRoles] = useState<TMultipleSelectValue>(
        initialAnnouncement ? initialAnnouncement.targetRoles : [],
    );
    const companyLanguages = userStore.payload!.companyLanguages;

    const langKeyMap: Record<string, keyof Languages> = {
        en: 'textEnglish',
        et: 'textEstonian',
        fi: 'textFinnish',
        lv: 'textLatvian',
        lt: 'textLithuanian',
        pl: 'textPolish',
        sv: 'textSwedish',
        ru: 'textRussian',
        ja: 'textJapanese',
        vi: 'textVietnamese',
    };

    const [period, setPeriod] = useState<DateRange>(
        initialAnnouncement
            ? {
                from: new Date(initialAnnouncement.startAt),
                to: new Date(initialAnnouncement.endAt),
            }
            : { from: new Date(), to: new Date() },
    );

    const [activeField, setActiveField] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const [texts, setTexts] = useState<Record<string, string>>(() => {
        const obj: Record<string, string> = {};

        companyLanguages.forEach((lang) => {
            const key = langKeyMap[lang];

            obj[lang] = key ? initialAnnouncement?.languages?.[key] ?? '' : '';
        });

        return obj;
    });

    const updateText = (lang: string, value: string) => {
        setTexts((prev) => ({ ...prev, [lang]: value }));
        setActiveField(lang);
    };

    const languageMap = companyLanguages.reduce(
        (acc, lang) => ({
            ...acc,
            [lang]: {
                setter: (value: string) =>
                    setTexts((prev) => ({ ...prev, [lang]: value })),
            },
        }),
        {} as Record<string, { setter: (value: string) => void }>,
    );

    const handleSave = () => {
        setError(null);

        const today = new Date();

        today.setHours(0, 0, 0, 0);

        if (!period?.from || !period?.to) {
            setError(labelStore.translate('announcement.modal.error_missing_dates'));

            return;
        }

        if (!targetRoles || targetRoles.length === 0) {
            setError(labelStore.translate('announcement.modal.error_missing_roles'));

            return;
        }

        const languages: Partial<Languages> = {};

        Object.entries(langKeyMap).forEach(([lang, key]) => {
            if (texts[lang]) {
                languages[key] = texts[lang];
            }
        });

        store.saveAnnouncement({
            languages,
            startAt: formatToYearAndMonthAndDayWithDateInput(period.from),
            endAt: formatToYearAndMonthAndDayWithDateInput(period.to),
            targetRoles,
            isAppLevel: isAppLevelAnnouncement,
        });
    };

    return (
        <Modal size="large">
            <ModalHeader>
                <h4>
                    {store.variables.openModal === 'edit-announcement' ? (
                        isAppLevelAnnouncement
                            ? labelStore.translate('announcement.modal.edit_app_level')
                            : labelStore.translate('announcement.modal.edit_company')
                    ) : (
                        isAppLevelAnnouncement
                            ? labelStore.translate('announcement.modal.add_app_level')
                            : labelStore.translate('announcement.modal.add_company')
                    )}
                </h4>
            </ModalHeader>

            <ModalContent>
                <div className="text">
                    <Datepicker
                        field={[
                            {
                                label: labelStore.translate('announcement.modal.start_date'),
                                id: 'announcement-start-date',
                                name: 'announcement-start-date',
                            },
                            {
                                label: labelStore.translate('announcement.modal.end_date'),
                                id: 'announcement-end-date',
                                name: 'announcement-end-date',
                            },
                        ]}
                        type="range"
                        format="dd.MM.yyyy"
                        value={period}
                        fromDate={new Date()}
                        onDateChange={(value: DateRange | undefined): void => {
                            const today = new Date();

                            today.setHours(0, 0, 0, 0);

                            const valid: DateRange = {
                                from: value?.from ?? today,
                                to: value?.to ?? today,
                            };

                            setPeriod(valid);
                        }}
                        locale={getLocaleFromLanguage(userStore.payload?.language)}
                    />

                    <Select
                        label={labelStore.translate('announcement.modal.target_roles')}
                        defaultValue={targetRoles}
                        onChange={(value): void => {
                            if (!value) {
                                return;
                            }
                            setTargetRoles(value as TMultipleSelectValue);
                        }}
                        options={[
                            ...store.payload!.targetRoles.map((role) => ({
                                label: store.label(role),
                                value: role,
                            })),
                        ]}
                        id="announcement-modal-employee-segment"
                        name="announcement-modal-employee-segment"
                        type={'multiple'}
                    />

                    {error && (
                        <p style={{ color: 'red', marginTop: '4px' }}>{error}</p>
                    )}

                    {companyLanguages.map((lang) => (
                        <div
                            key={lang}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginTop: '12px',
                                position: 'relative',
                            }}
                        >
                            <TextField
                                label={
                                    labelStore.translate(`announcement.modal.text_${lang}`) ||
                                    lang.toUpperCase()
                                }
                                value={texts[lang] || ''}
                                onChange={(value) => updateText(lang, value)}
                                onFocus={() => setActiveField(lang)}
                                id={`announcement-text-${lang}`}
                                name={`announcement-text-${lang}`}
                                element="textarea"
                                attributes={{
                                    style: { resize: 'vertical' },
                                }}
                            />

                            {activeField === lang && (
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                    }}
                                >
                                    {getTooltipButton({
                                        icon: 'translate',
                                        tooltip: labelStore.translate('announcement.modal.translate_tooltip'),
                                        onClick: async () =>
                                            await store.translateTextForLanguages(
                                                lang,
                                                texts[lang],
                                                companyLanguages,
                                                languageMap,
                                            ),
                                    })}

                                    {getTooltipButton({
                                        icon: 'sparkles',
                                        tooltip: labelStore.translate('announcement.modal.add_emoji'),
                                        onClick: () => {
                                            setEmojiTargetLang(lang);
                                            setEmojiPickerOpen((prev) => !prev);
                                        },
                                    })}
                                </div>
                            )}

                            {isEmojiPickerOpen && emojiTargetLang === lang && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        zIndex: 999,
                                        top: '100%',
                                        right: 0,
                                    }}
                                >
                                    <EmojiPicker
                                        onEmojiClick={(emojiObject) => {
                                            const emoji = emojiObject.emoji;

                                            updateText(lang, (texts[lang] || '') + emoji);
                                            setEmojiPickerOpen(false);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </ModalContent>

            <ModalFooter>
                <Grid wrap={['xs']} justify={['between-xs']}>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <ModalCloser>
                            <Button
                                block
                                color="white"
                                text={
                                    labelStore.translate('announcement.modal.cancel') || 'Cancel'
                                }
                            />
                        </ModalCloser>
                    </GridColumn>
                    <GridColumn width={['xs-12', 'sm-6']}>
                        <Button
                            block
                            text={labelStore.translate('announcement.modal.save')}
                            onClick={handleSave}
                        />
                    </GridColumn>
                </Grid>
            </ModalFooter>
        </Modal>
    );
});
